export default function LegalProtectionSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Legal Protection When Selling With Tenants
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling tenant occupied property requires careful attention to Texas landlord-tenant laws. We ensure full compliance to protect everyone involved.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Texas Landlord-Tenant Law Compliance</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you sell rental property with tenants in place, Texas law requires specific procedures to protect tenant rights. Our title company ensures every legal requirement is met, including proper notifications, lease transfers, and security deposit handling.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We've handled over 200 tenant occupied property transactions, and we understand the legal complexities inside and out. You can be confident that everything will be done correctly and legally.
            </p>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-teal-600">
                <div className="flex items-start space-x-4">
                  <i className="ri-file-text-fill text-teal-600 text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Tenant Notification Requirements</h4>
                    <p className="text-gray-700">
                      Texas law requires proper written notice to tenants when property ownership changes. We ensure all notifications are delivered correctly and documented.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-teal-600">
                <div className="flex items-start space-x-4">
                  <i className="ri-file-shield-fill text-teal-600 text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Lease Agreement Transfers</h4>
                    <p className="text-gray-700">
                      All existing lease agreements are properly transferred to the new owner. Tenants' rights under their leases are fully protected and honored.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-teal-600">
                <div className="flex items-start space-x-4">
                  <i className="ri-money-dollar-box-fill text-teal-600 text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Security Deposit Handling</h4>
                    <p className="text-gray-700">
                      Security deposits must be properly transferred and documented. Texas law has strict requirements, and our title company ensures full compliance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/056de6689fa40b4d5b9838840c4310d4.jpg"
              alt="Legal Protection Tenant Occupied Property"
              className="w-full h-auto rounded-lg shadow-2xl object-cover"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Legal Protections We Provide</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Title Insurance Coverage</h4>
                <p className="text-gray-700">
                  Comprehensive title insurance protects against any future claims or defects in the title, giving you peace of mind long after closing.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-user-follow-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Tenant Rights Protection</h4>
                <p className="text-gray-700">
                  All tenant rights under Texas law are respected and protected. Proper notices, lease transfers, and security deposit handling are guaranteed.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Complete Documentation</h4>
                <p className="text-gray-700">
                  Every document is prepared correctly by the title company—deeds, lease transfers, closing statements, and all required legal paperwork.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-bank-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Escrow Protection</h4>
                <p className="text-gray-700">
                  All funds are held in escrow by the title company until all conditions are met, ensuring secure handling of money for both parties.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-3-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Fair Housing Compliance</h4>
                <p className="text-gray-700">
                  All transactions comply with federal and state fair housing laws, protecting you from any potential discrimination claims.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-search-fill text-teal-600 text-2xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Title Search & Clearance</h4>
                <p className="text-gray-700">
                  Comprehensive title search identifies and resolves any liens, judgments, or encumbrances before closing.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-teal-50 border-l-4 border-teal-600 p-8 rounded-lg">
          <div className="flex items-start space-x-4">
            <i className="ri-information-fill text-teal-600 text-4xl flex-shrink-0"></i>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Why Legal Protection Matters</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Selling tenant occupied property without proper legal procedures can expose you to lawsuits from tenants, title defects, and other costly problems. Some buyers try to cut corners to save money, but this puts you at serious risk.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We never cut corners. Every transaction goes through a title company, every legal requirement is met, and every party is fully protected. This commitment to doing things the right way has earned us the trust of hundreds of landlords throughout El Paso.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}